import { MixpanelEvents } from '@/constants/mixpanelEvents';
import { mixpanelService } from '@/services/mixpanelService';
import type { ICheckoutContext } from '@/types/context';
import { getCheckoutDistinctId, getMixpanelProperties } from '@/utils/mixpanelProperties';
import type { EventData } from '@/services/iframeMessenger';
import type { PaymentMethodEnum } from '@/types/common';

const CHECKOUT_OPENED_AT_KEY = 'checkout_opened_at';

let identifiedDistinctId: string | undefined;

export const identifyCheckoutUser = (context?: ICheckoutContext): void => {
  if (identifiedDistinctId) return;

  const distinctId = getCheckoutDistinctId(context);
  if (!distinctId) return;

  identifiedDistinctId = distinctId;
  mixpanelService.identify(distinctId);
};

const getCheckoutLoadTime = (): number | undefined => {
  const checkoutOpenedAt = Number(sessionStorage.getItem(CHECKOUT_OPENED_AT_KEY));

  if (!Number.isFinite(checkoutOpenedAt)) {
    return undefined;
  }

  return Math.max(0, Math.round(performance.now() - checkoutOpenedAt));
};

/**
 * Module-scoped guard: a session = one app load = one presentation. The event
 * now fires from an effect that waits for the express wallets to settle, and
 * `<Payment>` REMOUNTS whenever the template swaps (PREFERRED → DEFAULT on a
 * wallet failure, or via "Use other payment method"), so a per-component ref
 * would let the same session report twice.
 */
let checkoutPresentedFired = false;

/**
 * `payment_methods` overrides the BE-offered list with the methods actually on
 * screen — see `getPresentedPaymentMethods`. Omitted, the default (everything
 * the BE returned) stands.
 */
export const trackCheckoutPresented = (
  session_id: string,
  context?: ICheckoutContext,
  payment_methods?: string[]
): void => {
  if (checkoutPresentedFired) return;
  checkoutPresentedFired = true;
  identifyCheckoutUser(context);
  mixpanelService.track(
    MixpanelEvents.CHECKOUT_PRESENTED,
    getMixpanelProperties(context, {
      session_id,
      load_time: getCheckoutLoadTime(),
      ...(payment_methods && { payment_methods }),
    }) as Record<string, unknown>
  );
};

/** Test-only escape hatch — DO NOT call from production code. */
export const __resetCheckoutPresentedForTests = (): void => {
  checkoutPresentedFired = false;
};

/**
 * CHECKOUT_OPENED fires before `/checkout/v3` resolves, so the context has no
 * publisher yet (ACDEV-29850). `publisher_id` is taken from the boot's
 * `checkoutTheme.publisherId` instead and only overrides the context-derived
 * value when present. `parsed_url` is always true: V3 cannot open without a
 * parsedUrl. The stored timestamp is the start of `load_time` on
 * CHECKOUT_PRESENTED.
 */
export const trackCheckoutOpened = (session_id: string, context?: ICheckoutContext, publisher_id?: string): void => {
  markCheckoutOpened();
  trackCheckoutOpenedEvent(session_id, context, publisher_id);
};

/**
 * For flows that send CHECKOUT_OPENED only once their data has loaded: call
 * this at init so `load_time` still covers the data fetch.
 */
export const markCheckoutOpened = (): void => {
  sessionStorage.setItem(CHECKOUT_OPENED_AT_KEY, performance.now().toString());
};

export const trackCheckoutOpenedEvent = (
  session_id: string,
  context?: ICheckoutContext,
  publisher_id?: string
): void => {
  mixpanelService.track(
    MixpanelEvents.CHECKOUT_OPENED,
    getMixpanelProperties(context, {
      session_id,
      parsed_url: true,
      ...(publisher_id ? { publisher_id } : {}),
    }) as Record<string, unknown>
  );
};

/**
 * Pay-CTA idle-pulse analytics (Sprint 87, A/B test attribution).
 *
 * Module-scoped guard so the event fires AT MOST ONCE per checkout
 * session — even across re-mounts of `<PayButton>`, template fallbacks
 * (express → DEFAULT), or re-renders triggered by interaction state.
 * Reset is NOT exposed by design: a session = one app load = one event.
 */
let payCtaPulseRenderedFired = false;

export const trackPayCtaPulseRendered = (context?: ICheckoutContext): void => {
  if (payCtaPulseRenderedFired) return;
  payCtaPulseRenderedFired = true;
  mixpanelService.track(
    MixpanelEvents.PAY_CTA_PULSE_RENDERED,
    getMixpanelProperties(context, { session_id: context?.sessionId }) as Record<string, unknown>
  );
};

/** Test-only escape hatch — DO NOT call from production code. */
export const __resetPayCtaPulseRenderedForTests = (): void => {
  payCtaPulseRenderedFired = false;
};

export const trackPayButtonClicked = (context: ICheckoutContext, additionalProperties?: Partial<EventData>): void => {
  const properties = getMixpanelProperties(context, {
    payment_method: additionalProperties?.paymentMethodName ?? context.paymentMethodSelected,
    session_id: context.sessionId,
    isSavedPaymentMethodUsed: additionalProperties?.isSavedPaymentMethodUsed ?? false,
    isNewPaymentMethodSaved: additionalProperties?.isNewPaymentMethodSaved ?? false,
  }) as Record<string, unknown>;

  mixpanelService.track(MixpanelEvents.PAY_BUTTON_CLICKED, {
    ...properties,
  });
};

export const trackPaymentChoose = (
  context: ICheckoutContext,
  paymentMethod: PaymentMethodEnum,
  previousPaymentMethod: PaymentMethodEnum
): void => {
  const selectedPaymentMethod = context.paymentMethodsSettings?.find((pm) => pm.paymentMethodName === paymentMethod);
  mixpanelService.track(MixpanelEvents.PAYMENT_CHOOSE, {
    ...(getMixpanelProperties(context, {
      session_id: context.sessionId,
      payment_method: paymentMethod,
      payment_provider: selectedPaymentMethod?.provider,
    }) as Record<string, unknown>),
    previous_payment_method: previousPaymentMethod,
  });
};

let cardEntryStartedFired = false;
let formEntryCompletedFired = false;

/** First focus or change on any Appcharge secured card field — once per checkout session. */
export const trackCardEntryStarted = (context?: ICheckoutContext): void => {
  if (cardEntryStartedFired) return;
  cardEntryStartedFired = true;
  mixpanelService.track(
    MixpanelEvents.CARD_ENTRY_STARTED,
    getMixpanelProperties(context, { session_id: context?.sessionId }) as Record<string, unknown>
  );
};

/** Appcharge card form first reached a payable (Pay-enabled) state — once per checkout session. */
export const trackFormEntryCompleted = (context?: ICheckoutContext): void => {
  if (formEntryCompletedFired) return;
  formEntryCompletedFired = true;
  mixpanelService.track(
    MixpanelEvents.FORM_ENTRY_COMPLETED,
    getMixpanelProperties(context, { session_id: context?.sessionId }) as Record<string, unknown>
  );
};

/** Test-only escape hatch — DO NOT call from production code. */
export const __resetCardEntryTrackingForTests = (): void => {
  cardEntryStartedFired = false;
  formEntryCompletedFired = false;
};
